export type BannerMode = 'full' | 'disabled';

let bannerModePromise: Promise<BannerMode> | undefined;

interface FrameSample {
  medianMs: number;
  slowFrameRatio: number;
  fps: number;
}

export function chooseBannerMode(): Promise<BannerMode> {
  bannerModePromise ??= determineBannerMode();
  return bannerModePromise;
}

async function determineBannerMode(): Promise<BannerMode> {
  // 1. Accesibilidad
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return 'disabled';
  }

  // 2. Hardware
  const cores = navigator.hardwareConcurrency ?? 4;
  const memory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
  const capabilityIsLow = cores <= 2 || memory !== undefined && memory <= 2;

  // 3. Medición de frames (1 segundo)
  const frames = await measureVisibleFrames(1_000);
  const isPerformancePoor =
    capabilityIsLow ||
    frames.medianMs > 25 || 
    frames.slowFrameRatio >= 0.05;

  if (isPerformancePoor) {
    return 'disabled';
  }

  return 'full';
}

function measureVisibleFrames(durationMs: number): Promise<FrameSample> {
  return new Promise((resolve) => {
    const intervals: number[] = [];
    let previous: number | undefined;
    let started: number | undefined;

    const sample = (timestamp: number) => {
      if (document.visibilityState !== 'visible') {
        requestAnimationFrame(sample);
        return;
      }

      started ??= timestamp;
      if (previous !== undefined) intervals.push(timestamp - previous);
      previous = timestamp;

      if (timestamp - started < durationMs) {
        requestAnimationFrame(sample);
        return;
      }

      intervals.sort((left, right) => left - right);
      const medianMs = intervals[Math.floor(intervals.length / 2)] ?? Infinity;
      const slowFrameRatio =
        intervals.filter((interval) => interval > 100).length / Math.max(intervals.length, 1);

      resolve({ medianMs, slowFrameRatio, fps: 1000 / medianMs });
    };

    requestAnimationFrame(sample);
  });
}